import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

import Icon from '../../components/AppIcon';
import SystemOverview from './components/SystemOverview';
import UserManagement from './components/UserManagement';
import DealManagement from './components/DealManagement';
import DisputeHandling from './components/DisputeHandling';
import SystemStatistics from './components/SystemStatistics';

const AdminPanelDashboard = () => {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Protect admin route
  useEffect(() => {
    if (!authLoading) {
      if (!userProfile) {
        navigate('/admin-authentication');
      } else if (userProfile?.role !== 'admin') {
        navigate('/unified-dashboard');
      } else {
        setLoading(false);
      }
    }
  }, [userProfile, authLoading, navigate]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/admin-authentication');
  };

  const tabs = [
    { id: 'overview', label: 'System Overview', icon: 'LayoutDashboard', label_ko: '시스템 개요' },
    { id: 'users', label: 'User Management', icon: 'Users', label_ko: '사용자 관리' },
    { id: 'deals', label: 'Deal Management', icon: 'Package', label_ko: '거래 관리' },
    { id: 'disputes', label: 'Dispute Handling', icon: 'AlertTriangle', label_ko: '분쟁 처리' },
    { id: 'statistics', label: 'System Statistics', icon: 'BarChart3', label_ko: '시스템 통계' }
  ];

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Admin Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                <Icon name="Shield" size={24} color="white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground">OGADA Admin Panel</h1>
                <p className="text-xs text-muted-foreground">Platform Management & Oversight</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-foreground">{userProfile?.full_name}</p>
                <p className="text-xs text-muted-foreground">Administrator</p>
              </div>
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 px-4 py-2 bg-destructive/10 hover:bg-destructive/20 text-destructive rounded-lg transition-colors"
              >
                <Icon name="LogOut" size={16} />
                <span className="text-sm font-medium">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-1 overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                <span className="text-sm">{tab?.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && <SystemOverview />}
        {activeTab === 'users' && <UserManagement />}
        {activeTab === 'deals' && <DealManagement />}
        {activeTab === 'disputes' && <DisputeHandling />}
        {activeTab === 'statistics' && <SystemStatistics />}
      </main>
    </div>
  );
};

export default AdminPanelDashboard;